/**
 * AI commentary history — the last 12 generated commentaries, browsable
 * from the chat panel and re-loadable into the viewer without another
 * generation run. localStorage only (no server sync, no Drive).
 */

import { makeAutoObservable } from 'mobx';
import type { PericopeInfo } from '@skjv/common';
import type { ReportRunMeta } from './types';

const STORAGE_KEY = 'skjv-commentary-history';
const MAX_ENTRIES = 12;

export interface CommentaryHistoryEntry {
  id: string;
  pericopeTitle: string | null;
  reference: string;
  text: string;
  rawModelBody?: string;
  itIsWritten?: string;
  pericope?: PericopeInfo | null;
  intent?: string;
  generatedAt: string;
  /** V6.4 — passage per-verse KJV so recalled anchors keep their
   *  scripture-form labels. Absent on pre-V6.4 entries (pill fallback). */
  passageVerses?: Array<{ verse: number; text: string }>;
  /** Experimental — the reader's "Any thoughts?" seed notes that shaped
   *  this commentary. */
  readerThoughts?: string;
  /** What the run did — so a recalled commentary still reports the
   *  conditions it was made under. Absent on pre-existing entries. */
  reportMeta?: ReportRunMeta;
}

/** A stored commentary to load into the viewer — a history entry without
 *  its id, which a study's attached StudyAiCommentary also satisfies. */
export type StoredCommentary = Omit<CommentaryHistoryEntry, 'id'>;

function load(): CommentaryHistoryEntry[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

class CommentaryHistoryState {
  entries: CommentaryHistoryEntry[] = [];

  constructor() {
    makeAutoObservable(this);
    this.entries = load();
  }

  record(entry: StoredCommentary): void {
    // An identical commentary (same passage, same text) replaces its older
    // entry instead of stacking a duplicate.
    const next = this.entries.filter(
      (e) => !(e.reference === entry.reference && e.text === entry.text),
    );
    next.unshift({
      ...entry,
      id: `ch-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    });
    this.entries = next.slice(0, MAX_ENTRIES);
    this.persist();
  }

  remove(id: string): void {
    this.entries = this.entries.filter((e) => e.id !== id);
    this.persist();
  }

  private persist(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.entries));
    } catch {
      // Quota exceeded — history is a convenience, not ground truth.
    }
  }
}

export const commentaryHistory = new CommentaryHistoryState();
